// Shared colour classes for the Website 2.0 industry blocks (before/after
// timeline, challenge cards, mechanism cards, mini UI previews, segment
// cards, billing flow). The preview tints each industry page with its own
// accent (`data-th`); here an accent is one of the existing tone pairs, so a
// page passes e.g. accent="mint" for grocery. "brand" is the default Fydo
// look (peach soft + primary red).
//
//   soft    - pale background (preview panes, later challenge cards)
//   text    - accent-coloured text (step numbers, timeline markers)
//   solid   - strong background that carries white text
//   border  - accent border (dashed ticket, arrows, highlighted split)
//   ring    - soft halo around an accent dot
export type IndustryAccent = "brand" | "mint" | "lilac" | "sun" | "sky";

export const accentClasses: Record<
  IndustryAccent,
  { soft: string; text: string; solid: string; border: string; ring: string }
> = {
  brand: {
    soft: "bg-tone-peach",
    text: "text-primary",
    solid: "bg-primary",
    border: "border-primary",
    ring: "ring-tone-peach",
  },
  mint: {
    soft: "bg-tone-mint",
    text: "text-tone-mint-ink",
    solid: "bg-tone-mint-ink",
    border: "border-tone-mint-ink",
    ring: "ring-tone-mint",
  },
  lilac: {
    soft: "bg-tone-lilac",
    text: "text-tone-lilac-ink",
    solid: "bg-tone-lilac-ink",
    border: "border-tone-lilac-ink",
    ring: "ring-tone-lilac",
  },
  sun: {
    soft: "bg-tone-sun",
    text: "text-tone-sun-ink",
    solid: "bg-tone-sun-ink",
    border: "border-tone-sun-ink",
    ring: "ring-tone-sun",
  },
  sky: {
    soft: "bg-tone-sky",
    text: "text-tone-sky-ink",
    solid: "bg-tone-sky-ink",
    border: "border-tone-sky-ink",
    ring: "ring-tone-sky",
  },
};

// The five customer groups Fydo builds from visits and spend. Each group
// keeps one colour everywhere it appears (segment cards, the legend dots in
// a mini UI preview): a soft chip with its own ink, and the ink as a solid
// (avatar, top rule, legend dot).
export type CustomerSegment = "new" | "repeat" | "vip" | "atRisk" | "lost";

export const segmentClasses: Record<
  CustomerSegment,
  { chip: string; solid: string; border: string; card: string }
> = {
  new: {
    chip: "bg-tone-mint text-tone-mint-ink",
    solid: "bg-tone-mint-ink",
    border: "border-tone-mint-ink",
    card: "bg-white",
  },
  repeat: {
    chip: "bg-tone-sky text-tone-sky-ink",
    solid: "bg-tone-sky-ink",
    border: "border-tone-sky-ink",
    card: "bg-white",
  },
  vip: {
    chip: "bg-tone-lilac text-tone-lilac-ink",
    solid: "bg-tone-lilac-ink",
    border: "border-tone-lilac-ink",
    card: "bg-white",
  },
  atRisk: {
    chip: "bg-tone-sun text-tone-sun-ink",
    solid: "bg-tone-sun-ink",
    border: "border-tone-sun-ink",
    card: "bg-cream",
  },
  lost: {
    chip: "bg-line text-ink-muted",
    solid: "bg-ink-muted",
    border: "border-dashed border-ink-muted",
    card: "bg-paper",
  },
};
